import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Brain, BookOpen, Network, Loader, ChevronLeft, ChevronRight } from 'lucide-react';
import ReactFlow from 'reactflow';
import 'reactflow/dist/style.css';
import '../styles/flashcards.css';
import Quiz from './Quiz';
import Flashcards from './Flashcards';
import ConceptMap from './ConceptMap';

const TabNavigation = ({ activeTab, onTabChange, isDarkMode }) => {
    const tabs = [
        { id: 'quiz', label: 'Quiz', icon: <Brain size={20} /> },
        { id: 'flashcards', label: 'Flashcards', icon: <BookOpen size={20} /> },
        { id: 'concept-map', label: 'Concept Map', icon: <Network size={20} /> }
    ];

    return (
        <div className={`border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex space-x-1">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => onTabChange(tab.id)}
                            className={`
                                px-6 py-3 flex items-center gap-2 transition-all
                                ${activeTab === tab.id 
                                    ? `${isDarkMode ? 'border-b-2 border-sand text-sand' : 'border-b-2 border-sand text-gray-900'}`
                                    : `${isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}
                            `}
                        >
                            {tab.icon}
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

const ErrorMessage = ({ message }) => (
    <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-700 rounded-lg">
        {message}
    </div>
);

const LoadingSpinner = () => (
    <div className="flex justify-center items-center h-64">
        <Loader className="animate-spin text-sand" size={32} />
    </div>
);

const LoadingMessage = ({ activeTab, quiz, flashcards, conceptMap, isDarkMode }) => {
    if ((activeTab === 'quiz' && quiz?.questions) ||
        (activeTab === 'flashcards' && flashcards?.flashcards) ||
        (activeTab === 'concept-map' && conceptMap?.nodes)) {
        return null;
    }

    return (
        <div className="text-center py-12">
            <p className={`text-lg ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {activeTab === 'quiz' && !quiz?.questions && 'Generating quiz...'}
                {activeTab === 'flashcards' && !flashcards?.flashcards && 'Generating flashcards...'}
                {activeTab === 'concept-map' && !conceptMap?.nodes && 'Generating concept map...'}
            </p>
        </div>
    );
};

const LectureAI = ({ isDarkMode, transcript }) => {
    const [activeTab, setActiveTab] = useState('quiz');
    const [quiz, setQuiz] = useState(null);
    const [flashcards, setFlashcards] = useState(null);
    const [conceptMap, setConceptMap] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (transcript && activeTab) {
            switch(activeTab) {
                case 'quiz':
                    if (!quiz) generateQuiz();
                    break;
                case 'flashcards':
                    if (!flashcards) generateFlashcards();
                    break;
                case 'concept-map':
                    if (!conceptMap) generateConceptMap();
                    break;
            }
        }
    }, [transcript, activeTab]);

    const generateQuiz = async () => {
        setLoading(true);
        setError(null);
        try {
            if (!transcript || transcript.trim() === '') {
                throw new Error('No lecture transcript available');
            }

            const response = await fetch('https://classify-alpha-a1e5331e0be0.herokuapp.com/api/generate-quiz', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ transcript: transcript.trim() }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate quiz');
            }

            const data = await response.json();
            setQuiz(data);
        } catch (error) {
            console.error('Error generating quiz:', error);
            setError(error.message || 'Failed to generate quiz. Please try again.');
        }
        setLoading(false);
    };

    const generateFlashcards = async () => {
        setLoading(true);
        setError(null);
        try {
            if (!transcript || transcript.trim() === '') {
                throw new Error('No lecture transcript available');
            }

            const response = await fetch('https://classify-alpha-a1e5331e0be0.herokuapp.com/api/generate-flashcards', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ transcript: transcript.trim() }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate flashcards');
            }

            const data = await response.json();
            setFlashcards(data);
        } catch (error) {
            console.error('Error generating flashcards:', error);
            setError(error.message || 'Failed to generate flashcards. Please try again.');
        }
        setLoading(false);
    };

    const generateConceptMap = async () => {
        setLoading(true);
        setError(null);
        try {
            if (!transcript || transcript.trim() === '') {
                throw new Error('No lecture transcript available');
            }

            const response = await fetch('https://classify-alpha-a1e5331e0be0.herokuapp.com/api/generate-concept-map', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ transcript: transcript.trim() }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate concept map');
            }

            const data = await response.json();
            setConceptMap(data);
        } catch (error) {
            console.error('Error generating concept map:', error);
            setError(error.message || 'Failed to generate concept map. Please try again.');
        }
        setLoading(false);
    };

    const handleQuizSubmit = (score) => {
        console.log('Quiz submitted with score:', score);
        // Add any additional handling needed for quiz submission
    };

    // API calls and data fetching logic

    return (
        <>
            <TabNavigation 
                activeTab={activeTab} 
                onTabChange={setActiveTab} 
                isDarkMode={isDarkMode} 
            />

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {error && <ErrorMessage message={error} />}
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <div>
                        {activeTab === 'quiz' && quiz?.questions && (
                            <Quiz 
                                isDarkMode={isDarkMode} 
                                questions={quiz.questions} 
                                onSubmit={handleQuizSubmit} 
                            />
                        )}

                        {activeTab === 'flashcards' && flashcards?.flashcards && (
                            <Flashcards 
                                isDarkMode={isDarkMode} 
                                flashcards={flashcards.flashcards} 
                            />
                        )}

                        {activeTab === 'concept-map' && conceptMap?.nodes && (
                            <ConceptMap 
                                isDarkMode={isDarkMode} 
                                nodes={conceptMap.nodes}
                                edges={conceptMap.edges}
                            />
                        )}

                        {!loading && !error && (
                            <LoadingMessage 
                                activeTab={activeTab} 
                                quiz={quiz} 
                                flashcards={flashcards} 
                                conceptMap={conceptMap} 
                                isDarkMode={isDarkMode} 
                            />
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default LectureAI;